import { useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useController, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'shared/hooks';
import { useVendorSearch } from 'shared/queries';
import { Brands, HTMLUListElementAttributes, VendorLookup } from 'shared/types';

interface VendorAutocompleteProps {
  name: `${string}` | `${string}.${string}` | `${string}.${number}`;
  defaultValue: string;
  onSelect: (vendor: string | Record<keyof VendorLookup, string> | null) => void;
  onClear: () => void;
  error?: boolean;
  helperText?: string;
  readOnly?: boolean;
}

export function VendorAutocomplete({
  name,
  onSelect,
  onClear,
  error,
  helperText,
  defaultValue = '',
  readOnly,
}: VendorAutocompleteProps) {
  const { watch } = useFormContext();
  const {
    field: { value, ref, onChange },
  } = useController({
    name,
    rules: { required: { message: 'Vendor Name is required', value: true } },
    defaultValue,
  });
  const [isFirstOnInputChangeCall, setIsFirstOnInputChangeCall] = useState<boolean>(true);
  const { brand } = useParams<{ brand: Brands }>();
  const search = (watch(name) as unknown) as string;
  const debouncedSearch = useDebounce(search, 300);
  const { data, isLoading, isFetching } = useVendorSearch(debouncedSearch, brand);
  const loading = isLoading || isFetching;

  return (
    <Autocomplete
      id={name}
      disabled={readOnly}
      inputValue={value}
      onChange={async (_, newValue, reason) => {
        onSelect(newValue);
        if (reason === 'clear') {
          onClear();
        }
      }}
      onInputChange={(_, newValue, reason) => {
        if (reason === 'clear') {
          onClear();
          return;
        }
        if (isFirstOnInputChangeCall && newValue === '') {
          setIsFirstOnInputChangeCall(false);
          return;
        } else if (isFirstOnInputChangeCall) {
          setIsFirstOnInputChangeCall(false);
        }
        onChange(newValue);
      }}
      filterOptions={(options, params) => {
        if (params.inputValue !== '') {
          options.push({
            vendorname: '',
            brand,
            uniqueid: '',
            vendorid: '',
            inputValue: params.inputValue,
            addDisplayLabel: `Add "${params.inputValue}"`,
          });
        }
        return options;
      }}
      freeSolo
      selectOnFocus
      clearOnBlur
      isOptionEqualToValue={(option, value) =>
        option.vendorname === value.vendorname && option.vendorid === value.vendorid
      }
      getOptionLabel={option => option.inputValue || option.vendorname}
      renderOption={(props, option) => <li {...props}>{option.addDisplayLabel || option.vendorname}</li>}
      options={data || []}
      loading={loading}
      ref={ref}
      fullWidth
      data-testid={`${name}-combobox`}
      ListboxProps={{ 'data-testid': `${name}-listbox` } as HTMLUListElementAttributes}
      renderInput={params => (
        <TextField
          {...params}
          label='Vendor'
          variant='outlined'
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color='inherit' size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
